import { useSyncExternalStore } from 'react'
import { useThree } from '@react-three/fiber'
import { useKTX2 } from '@react-three/drei'
import type { CompressedTexture, WebGLRenderer } from 'three'
import { KTX2Loader } from 'three-stdlib'
import { preload as preloadSuspenseEntry } from 'suspend-react'
import { textureAssetCompressedUrls } from './assetStore'
import { withRetry } from './assetFetcher'
import {
    createDeferred,
    createPreloadQueue,
    type Deferred,
    type PreloadJob,
} from './preloadQueue'

const MAX_PARALLEL_DOWNLOADS = 2
const ktx2BasisPath = '/basis/'

const ktx2Deferreds = new Map<string, Deferred<[CompressedTexture]>>(
    textureAssetCompressedUrls.map((url) => [
        url,
        createDeferred<[CompressedTexture]>(),
    ]),
)

const ktx2Preload = createPreloadQueue({
    name: 'ktx2Textures',
    total: textureAssetCompressedUrls.length,
    concurrency: MAX_PARALLEL_DOWNLOADS,
})

function createKtx2Job(
    loader: KTX2Loader,
    url: string,
    deferred: Deferred<[CompressedTexture]>,
): PreloadJob {
    return {
        label: url,
        run: async () => {
            deferred.resolve([
                await withRetry(url, () => loader.loadAsync(url) as Promise<CompressedTexture>),
            ])
        },
    }
}

let ktx2PreloadStarted = false

export function startKtx2Preload(gl: WebGLRenderer) {
    if (ktx2PreloadStarted) return
    ktx2PreloadStarted = true

    const loader = new KTX2Loader()
    loader.detectSupport(gl)
    loader.setTranscoderPath(ktx2BasisPath)

    const jobs: PreloadJob[] = []
    for (const [url, deferred] of ktx2Deferreds) {
        preloadSuspenseEntry(() => deferred.promise, [KTX2Loader, url])
        jobs.push(createKtx2Job(loader, url, deferred))
    }

    ktx2Preload.start(jobs)
}

export function retryFailedKtx2Textures() {
    ktx2Preload.retryFailed()
}

export function useCompressedTexture(url: string) {
    return useKTX2(url, ktx2BasisPath)
}

export function useKtx2PreloadStatus() {
    return useSyncExternalStore(
        ktx2Preload.subscribe,
        ktx2Preload.getSnapshot,
        ktx2Preload.getSnapshot,
    )
}

export function useKtx2PreloadBridge() {
    const { gl } = useThree()
    startKtx2Preload(gl)
}
